// // src/api/interceptors/response.interceptor.ts

// /**
//  * Response Interceptor
//  *
//  * WHY: Handle responses consistently
//  * WHAT: Logging, timing, error transformation
//  * BENEFIT:
//  *   - Response timing for performance monitoring
//  *   - Consistent success logging
//  *   - Error breadcrumbs for debugging
//  */

// import { type AxiosResponse, AxiosError } from "axios";
// import { errorLogger } from "@/core/errors/utils";
// import { errorReporter } from "@/core/errors/monitoring";
// import { logger } from "@/core/utils";
// /**
//  * Response interceptor
//  * Runs for successful responses
//  */
// export const responseInterceptor = (response: AxiosResponse): AxiosResponse => {
//   // Calculate request duration
//   const startTime = (response.config as any).__startTime;
//   const duration = startTime ? Date.now() - startTime : 0;

//   // Log response
//   errorLogger.debug(
//     `← ${response.status} ${response.config.method?.toUpperCase()} ${response.config.url}`,
//     { duration: `${duration}ms` },
//   );

//   // Warn about slow requests
//   if (duration > 3000) {
//     errorLogger.warn(`Slow request: ${response.config.url}`, {
//       duration: `${duration}ms`,
//     });
//   }

//   // Add breadcrumb
//   errorReporter.addBreadcrumb({
//     category: "http",
//     message: `Response ${response.status}`,
//     level: "info",
//     data: {
//       url: response.config.url,
//       status: response.status,
//       duration,
//     },
//   });
//   logger.info("error in responseInterceptor", response);
//   return response.data;
// };

// /**
//  * Response error interceptor
//  * Runs for error responses
//  */
// export const responseErrorInterceptor = (error: AxiosError): Promise<never> => {
//   // Calculate request duration
//   const startTime = (error.config as any)?.__startTime;
//   const duration = startTime ? Date.now() - startTime : 0;

//   // Add error breadcrumb
//   errorReporter.addBreadcrumb({
//     category: "http",
//     message: `Error ${error.response?.status ?? "network"}`,
//     level: "error",
//     data: {
//       url: error.config?.url,
//       status: error.response?.status,
//       message: error.message,
//       duration,
//     },
//   });
//   // Reject with original error (will be handled by AxiosErrorHandler)
//   return Promise.reject(error);
// };

// src/api/interceptors/response.interceptor.ts

/**
 * Response Interceptor - FIXED
 *
 * CRITICAL FIX: Returns the FULL AxiosResponse, not response.data
 *
 * WHY THIS MATTERS:
 * ┌─────────────────────────────────────────────────────────────┐
 * │ BEFORE (BROKEN):                                           │
 * │ interceptor returns response.data                          │
 * │ → { success: true, data: { user: {...} } }                │
 * │                                                            │
 * │ HttpService does: response.data                            │
 * │ → accesses the `data` KEY of your API response             │
 * │ → { user: {...} }                                          │
 * │                                                            │
 * │ This ACCIDENTALLY works for success but breaks typing      │
 * │ and makes error responses unpredictable.                   │
 * │                                                            │
 * │ AFTER (FIXED):                                             │
 * │ interceptor returns full AxiosResponse                     │
 * │ → { data: { success: true, data: {...} }, status: 200 }   │
 * │                                                            │
 * │ HttpService does: response.data                            │
 * │ → { success: true, data: { user: {...} } }                │
 * │ → Correct! The full API response body.                    │
 * └─────────────────────────────────────────────────────────────┘
 */

import type { AxiosResponse } from "axios";
import { errorReporter } from "@/core/errors/monitoring";
import { logger } from "@/core/utils";

export const responseInterceptor = (response: AxiosResponse): AxiosResponse => {
  // Calculate request duration
  const startTime = (response.config as any).__startTime;
  const duration = startTime ? Date.now() - startTime : 0;

  // Log response
  logger.debug(
    `← ${response.status} ${response.config.method?.toUpperCase()} ${response.config.url} (${duration}ms)`,
  );

  // Warn about slow requests
  if (duration > 3000) {
    logger.warn(`🐢 Slow request: ${response.config.url} (${duration}ms)`);
  }

  // Add breadcrumb for error tracking
  errorReporter.addBreadcrumb({
    category: "http",
    message: `Response ${response.status}`,
    level: "info",
    data: {
      url: response.config.url,
      status: response.status,
      duration,
    },
  });

  // ✅ Return FULL response - let HttpService handle unwrapping
  return response;
};
